import { FC } from 'react';
import { MATCH_REF_DENSITY } from '@leopart/calc';
import DistributionBars from '../../../shared/charts/DistributionBars';
import DensityScale from '../shared/DensityScale';
import Typhography from '../../../shared/Typhography/Typhography';
import { ITrainingMixGameDensity } from '../../../models/allenamenti';
import { formatMinutes } from './analysis.constants';
import { useGameDensityPanel } from './useGameDensityPanel';
import styles from './GameDensityPanel.module.scss';

interface IGameDensityPanelProps {
  /** `null` = nessun blocco di gioco nel periodo. */
  density: ITrainingMixGameDensity | null;
}

/**
 * Pannello «Densità di gioco — m²/giocatore» (ADR 029): minuti per range,
 * media ponderata sul tempo effettivo, % vs riferimento gara (~320), rapporto
 * tempo effettivo/durata, conteggio dei blocchi esclusi per dati mancanti e,
 * DENTRO ciascuna banda con minuti > 0, il breakdown per modalità
 * (continuo/intermittente/analitico) + la modalità complessiva del periodo —
 * stesso pattern del pannello «Dimensione dei gruppi» (F2, PlayerGroupPanel).
 * Le etichette/colori dei range vengono da @leopart/calc, non dal server.
 */
const GameDensityPanel: FC<IGameDensityPanelProps> = ({ density }) => {
  const { rangeBars, rangeDetails, totalFlowBars } = useGameDensityPanel(density);

  return (
    <div className={styles.card}>
      <Typhography type="h3" size="bold-14" className={styles.title}>
        Densità di gioco — m²/giocatore
      </Typhography>
      <Typhography size="regular-12" className={styles.subtitle}>
        Solo blocchi delle categorie di gioco. Minuti ponderati sul tempo
        effettivo quando inserito, altrimenti sulla durata del blocco.
      </Typhography>

      {!density ? (
        <p className={styles.empty}>Nessun blocco di gioco nel periodo.</p>
      ) : density.computedBlocks === 0 ? (
        <p className={styles.empty}>
          Blocchi di gioco presenti ({density.eligibleBlocks}) ma senza dati
          campo/giocatori: nessun calcolo possibile.
        </p>
      ) : (
        <>
          <DistributionBars data={rangeBars} valueFormatter={formatMinutes} />

          {/* La media del periodo posizionata sul righello dei range, con la
              tacca del riferimento gara: il "dove siamo" a colpo d'occhio. */}
          {density.weightedAvgDensity != null && (
            <div className={styles.scaleWrap}>
              <DensityScale value={density.weightedAvgDensity} />
            </div>
          )}

          {rangeDetails.length > 0 && (
            <div className={styles.rangeGrid}>
              {rangeDetails.map((range) => (
                <div key={range.id} className={styles.rangeCard}>
                  <div className={styles.rangeHeader}>
                    {/* Colore runtime dalla banda @leopart/calc, non un token statico. */}
                    <span className={styles.dot} style={{ background: range.color }} />
                    <span className={styles.rangeLabel}>{range.label}</span>
                    <span className={styles.rangeMinutes}>
                      {formatMinutes(range.minutes)}
                      {range.pct != null && ` · ${range.pct}%`}
                    </span>
                  </div>
                  <DistributionBars data={range.flowBars} valueFormatter={formatMinutes} />
                </div>
              ))}
            </div>
          )}

          <Typhography size="bold-12" className={styles.miniTitle}>
            Modalità complessiva (tutte le bande)
          </Typhography>
          <DistributionBars
            data={totalFlowBars}
            valueFormatter={formatMinutes}
            labelMode="value-percent"
          />

          <p className={styles.meta}>
            Media ponderata: <b>{density.weightedAvgDensity} m²/giocatore</b>
            {density.pctOfMatchRef != null && (
              <>
                {' — '}
                <b>{Math.round(density.pctOfMatchRef)}%</b> del riferimento gara
                (~{MATCH_REF_DENSITY} m²/g, 11c11 su 105×68)
              </>
            )}
            {density.effectiveMinutes > 0 && (
              <>
                <br />
                Tempo effettivo: <b>{density.effectiveMinutes}&apos;</b> su{' '}
                {density.plannedMinutes}&apos; di blocchi gioco
                {density.effectiveRatio != null && (
                  <>
                    {' '}
                    (<b>{Math.round(density.effectiveRatio)}%</b> di gioco reale)
                  </>
                )}
              </>
            )}
            {density.excludedBlocks > 0 && (
              <>
                <br />
                <b>{density.excludedBlocks}</b>{' '}
                {density.excludedBlocks === 1
                  ? 'blocco di gioco senza dati campo/giocatori (escluso dal calcolo)'
                  : 'blocchi di gioco senza dati campo/giocatori (esclusi dal calcolo)'}
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
};

export default GameDensityPanel;
